// @flow
import { createInternalMessage } from 'api/messages'
import { useLoadFormsAndDocuments } from 'queries/forms/useLoadForms'
import * as React from 'react'
import { useHistory } from 'react-router-dom'

import type { Customer } from '../../types/customer'
import SendResourceModal from './SendResourceModal'

type Props = {
  onHide: () => void,
}

function StartInternalForm({ onHide }: Props): React.Node {
  const history = useHistory()
  const { data: formsResult, isLoading: isFormsLoading } =
    useLoadFormsAndDocuments({
      internal_only: 'true',
      pageSize: 999,
    })
  const forms = formsResult?.results

  const options = React.useMemo(() => {
    return (
      forms?.map((form) => ({
        label: form.name,
        value: form.id,
        hasOfficeUseQuestions: false,
      })) || []
    )
  }, [forms])

  const createMessage = React.useCallback(
    async ({
      client,
      form,
      team,
      document,
    }: {
      client: Customer,
      team: string,
      form?: string,
      document?: string,
    }) => {
      return await createInternalMessage({
        customer: client.id,
        team,
        form,
        document,
      })
    },
    []
  )

  const onSubmit = React.useCallback(
    async ({
      setContactToSendForm,
      setPersonToSendForm,
      doSendResource,
      validateForm,
      setSubmitting,
    }) => {
      if (!validateForm()) return
      setSubmitting(true)
      const client = await setContactToSendForm()
      setPersonToSendForm(client)

      await doSendResource(client, false)
      setSubmitting(false)
    },
    []
  )

  const sendResource = React.useCallback(
    async ({ id, personToUse, teamDetails, selectedOption }) => {
      if (!teamDetails) return

      const isDocument = forms?.find((form) => form.id === id)?.type === 'pdf'

      const message = await createMessage({
        client: personToUse,
        team: teamDetails?.uid,
        form: isDocument ? undefined : id,
        document: isDocument ? id : undefined,
      })

      if (message.response?.id) {
        history.push(
          `/internal-form/${message.response.id}?messageCode=${message.message_code}`
        )
      }
    },
    [createMessage, forms, history]
  )

  return (
    <SendResourceModal
      forceDob
      isVerified
      customActionIcon={null}
      customActionText="Start"
      dropdownDisabled={!options?.length}
      dropdownLabel="Select a Form"
      dropdownPlaceholder="Select Form"
      heading="Start Internal Form"
      loading={isFormsLoading}
      onHide={onHide}
      onSubmit={onSubmit}
      options={options}
      sendResource={sendResource}
      subheading="Complete the following to start an internal form."
    />
  )
}

export default StartInternalForm
